import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, MessageCircle, Edit, Eye, Lock, Unlock, Loader2, Send, Clock, User, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RealTimeCollabView = ({ contracts }) => {
  const [selectedContract, setSelectedContract] = useState("");
  const [activeTab, setActiveTab] = useState("live");
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [editHistory, setEditHistory] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [events, setEvents] = useState([]);
  const [lockedSections, setLockedSections] = useState({});
  const token = localStorage.getItem("gpi_token");
  const eventsRef = useRef(null);

  useEffect(() => {
    if (selectedContract && token) {
      fetchSessionData();
    }
    return () => {
      if (ws) ws.close();
    };
  }, [selectedContract]);

  const fetchSessionData = async () => {
    try {
      const res = await axios.get(`${API}/realtime/sessions/${selectedContract}`);
      setActiveUsers(res.data.active_users || []);
      setEditHistory(res.data.recent_edits || []);
      setComments(res.data.comments || []);
    } catch (e) {
      console.log("No active sessions");
    }
  };

  const connectWebSocket = () => {
    if (!selectedContract || !token) {
      toast.error("Select a contract and sign in first");
      return;
    }
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const socket = new WebSocket(`${wsUrl}/api/realtime/ws/${selectedContract}?token=${token}`);

    socket.onopen = () => {
      setConnected(true);
      setWs(socket);
      toast.success("Connected to live session");
      addEvent({ type: "system", text: "Connected to collaboration session" });
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleWSMessage(data);
    };

    socket.onclose = () => {
      setConnected(false);
      setWs(null);
      addEvent({ type: "system", text: "Disconnected from session" });
    };

    socket.onerror = () => {
      toast.error("Connection error");
      setConnected(false);
    };
  };

  const handleWSMessage = (data) => {
    switch (data.type) {
      case "user_joined":
        setActiveUsers(data.active_users || []);
        addEvent({ type: "join", text: `${data.user?.name || 'User'} joined`, user: data.user });
        break;
      case "user_left":
        setActiveUsers(data.active_users || []);
        addEvent({ type: "leave", text: `${data.user?.name || 'User'} left`, user: data.user });
        break;
      case "edit_applied":
        setEditHistory(prev => [data.edit, ...prev.slice(0, 49)]);
        addEvent({ type: "edit", text: `${data.user?.name || 'User'} edited ${data.edit?.field || 'section'}`, user: data.user });
        break;
      case "new_comment":
        setComments(prev => [data.comment, ...prev]);
        addEvent({ type: "comment", text: `${data.comment?.user_name || 'User'} commented`, user: data.comment });
        break;
      case "section_locked":
        setLockedSections(prev => ({ ...prev, [data.section]: data.locked_by }));
        addEvent({ type: "lock", text: `${data.locked_by?.name || 'User'} locked ${data.section}` });
        break;
      case "section_unlocked":
        setLockedSections(prev => { const n = { ...prev }; delete n[data.section]; return n; });
        addEvent({ type: "unlock", text: `${data.unlocked_by?.name || 'User'} unlocked ${data.section}` });
        break;
      case "user_typing":
        addEvent({ type: "typing", text: `${data.user?.name || 'User'} is typing in ${data.section}`, temporary: true });
        break;
      default:
        break;
    }
  };

  const addEvent = (event) => {
    setEvents(prev => [{ ...event, timestamp: new Date().toISOString() }, ...prev.slice(0, 99)]);
  };

  const sendComment = async () => {
    if (!newComment.trim()) return;
    if (ws && connected) {
      ws.send(JSON.stringify({ type: "comment", text: newComment, section: "general" }));
    } else {
      try {
        await axios.post(`${API}/realtime/comments/${selectedContract}?text=${encodeURIComponent(newComment)}&section=general`);
        fetchSessionData();
      } catch (e) {
        toast.error("Failed to post comment");
      }
    }
    setNewComment("");
  };

  return (
    <div className="space-y-6" data-testid="realtime-collab-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Real-Time Collaboration</h1>
          <p className="text-zinc-500 mt-1">Live multi-user editing and commenting on contracts</p>
        </div>
        <div className="flex items-center gap-3">
          {connected ? (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 animate-pulse" data-testid="ws-connected-badge">
              <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 inline-block" /> Live
            </Badge>
          ) : (
            <Badge variant="outline" className="text-zinc-500">Disconnected</Badge>
          )}
          <Badge variant="outline" data-testid="active-users-count"><Users size={14} className="mr-1" /> {activeUsers.length} online</Badge>
        </div>
      </div>

      <div className="border-2 border-black bg-white p-6 brutal-shadow">
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 block">Select Contract</label>
            <select
              value={selectedContract}
              onChange={e => setSelectedContract(e.target.value)}
              className="w-full border-2 border-zinc-200 p-3 font-medium focus:border-black"
              data-testid="collab-contract-select"
            >
              <option value="">Choose a contract...</option>
              {(contracts || []).map(c => (
                <option key={c.id} value={c.id}>{c.project_name} ({c.framework})</option>
              ))}
            </select>
          </div>
          <Button
            onClick={connectWebSocket}
            disabled={!selectedContract || connected}
            className="bg-[#EAB308] text-white px-6 py-3"
            data-testid="connect-ws-btn"
          >
            {connected ? "Connected" : "Join Session"}
          </Button>
          {connected && (
            <Button
              onClick={() => { ws?.close(); setConnected(false); }}
              variant="outline"
              className="border-2 border-red-300 text-red-600 hover:bg-red-50"
              data-testid="disconnect-ws-btn"
            >
              Leave
            </Button>
          )}
        </div>
      </div>

      {selectedContract && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-zinc-100 border-2 border-zinc-200">
            <TabsTrigger value="live" data-testid="tab-live-feed">Live Feed</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">Comments ({comments.length})</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-edit-history">Edit History ({editHistory.length})</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-active-users">Active Users ({activeUsers.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="live" className="border-2 border-black brutal-shadow bg-white">
            <div className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Live Activity Feed</h3>
              
              {/* Comment input at top of live feed */}
              <div className="flex gap-3 mb-6 p-3 bg-zinc-50 border border-zinc-200">
                <input
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendComment()}
                  placeholder="Post a comment to the live feed..."
                  className="flex-1 border-2 border-zinc-200 px-4 py-2 focus:border-black bg-white text-sm"
                  data-testid="live-comment-input"
                />
                <Button onClick={sendComment} className="bg-black text-white" data-testid="live-send-comment-btn">
                  <Send size={16} className="mr-1" /> Send
                </Button>
              </div>

              <ScrollArea className="h-[400px]">
                {events.length === 0 && comments.length === 0 ? (
                  <div className="text-center py-12 text-zinc-400">
                    <Users size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="mb-2">No activity yet. Connect to a session to see live updates.</p>
                    <p className="text-sm">You can also post comments via the input above or from REST API.</p>
                  </div>
                ) : (
                  <div className="space-y-2" ref={eventsRef}>
                    {/* Show session events */}
                    {events.map((evt, i) => (
                      <div key={`evt-${i}`} className={`flex items-start gap-3 p-3 text-sm ${evt.temporary ? 'opacity-50' : ''} ${
                        evt.type === 'join' ? 'bg-emerald-50 border-l-4 border-emerald-400' :
                        evt.type === 'leave' ? 'bg-red-50 border-l-4 border-red-400' :
                        evt.type === 'edit' ? 'bg-blue-50 border-l-4 border-blue-400' :
                        evt.type === 'comment' ? 'bg-amber-50 border-l-4 border-amber-400' :
                        evt.type === 'lock' ? 'bg-purple-50 border-l-4 border-purple-400' :
                        'bg-zinc-50 border-l-4 border-zinc-300'
                      }`}>
                        <div className="flex-shrink-0 mt-0.5">
                          {evt.type === 'join' && <Users size={14} className="text-emerald-600" />}
                          {evt.type === 'leave' && <Users size={14} className="text-red-600" />}
                          {evt.type === 'edit' && <Edit size={14} className="text-blue-600" />}
                          {evt.type === 'comment' && <MessageCircle size={14} className="text-amber-600" />}
                          {evt.type === 'lock' && <Lock size={14} className="text-purple-600" />}
                          {evt.type === 'unlock' && <Unlock size={14} className="text-purple-400" />}
                          {evt.type === 'system' && <Eye size={14} className="text-zinc-400" />}
                        </div>
                        <span className="flex-1 break-words">{evt.text}</span>
                        <span className="text-xs text-zinc-400 whitespace-nowrap ml-2 flex-shrink-0">
                          {new Date(evt.timestamp).toLocaleDateString()} {new Date(evt.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    {/* Show persisted comments inline */}
                    {comments.length > 0 && (
                      <>
                        <div className="border-t border-zinc-200 pt-3 mt-3">
                          <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">Persisted Comments</p>
                        </div>
                        {comments.map((c, i) => (
                          <div key={`cmt-${i}`} className={`p-3 border-l-4 border-amber-400 bg-amber-50 text-sm ${c.resolved ? 'opacity-60' : ''}`} data-testid={`live-comment-${i}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <User size={12} className="text-amber-600" />
                                <span className="font-medium text-xs">{c.user_name}</span>
                                {c.section && <Badge variant="outline" className="text-xs py-0 h-4">{c.section}</Badge>}
                                {c.resolved && <Check size={12} className="text-emerald-500" />}
                              </div>
                              <span className="text-xs text-zinc-400 whitespace-nowrap">
                                {new Date(c.created_at).toLocaleDateString()} {new Date(c.created_at).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="mt-1 break-words">{c.text}</p>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="comments" className="border-2 border-black brutal-shadow bg-white">
            <div className="p-6">
              <div className="flex gap-3 mb-6">
                <input
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && sendComment()}
                  placeholder="Add a comment..."
                  className="flex-1 border-2 border-zinc-200 px-4 py-2 focus:border-black"
                  data-testid="comment-input"
                />
                <Button onClick={sendComment} className="bg-black text-white" data-testid="send-comment-btn">
                  <Send size={16} />
                </Button>
              </div>
              <ScrollArea className="h-[350px]">
                {comments.length === 0 ? (
                  <p className="text-center text-zinc-400 py-8">No comments yet. Add one above!</p>
                ) : (
                  <div className="space-y-3">
                    {comments.map((c, i) => (
                      <div key={i} className={`p-4 border ${c.resolved ? 'bg-zinc-50 border-zinc-200' : 'bg-white border-zinc-300'}`} data-testid={`comment-item-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-[#EAB308] rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {(c.user_name || "U")[0].toUpperCase()}
                            </div>
                            <span className="font-medium text-sm">{c.user_name}</span>
                            {c.section && <Badge variant="outline" className="text-xs">{c.section}</Badge>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock size={12} className="text-zinc-400" />
                            <span className="text-xs text-zinc-400" data-testid={`comment-date-${i}`}>
                              {new Date(c.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} at {new Date(c.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                            {c.resolved && <Check size={14} className="text-emerald-500" />}
                          </div>
                        </div>
                        <p className="text-sm leading-relaxed pl-8">{c.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="history" className="border-2 border-black brutal-shadow bg-white">
            <div className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Edit & Comment History</h3>
              <ScrollArea className="h-[400px]">
                {editHistory.length === 0 && comments.length === 0 ? (
                  <p className="text-center text-zinc-400 py-8">No history recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {/* Merge edits and comments into a single timeline sorted by date */}
                    {[
                      ...editHistory.map(e => ({ ...e, _type: 'edit', _time: e.timestamp })),
                      ...comments.map(c => ({ ...c, _type: 'comment', _time: c.created_at }))
                    ].sort((a, b) => new Date(b._time) - new Date(a._time)).map((item, i) => (
                      <div key={i} className={`p-3 border ${item._type === 'edit' ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}`} data-testid={`history-item-${i}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            {item._type === 'edit' ? <Edit size={14} className="text-blue-500" /> : <MessageCircle size={14} className="text-amber-500" />}
                            <span className="font-medium text-sm">{item.user_name || 'User'}</span>
                            <Badge variant="outline" className="text-xs py-0">
                              {item._type === 'edit' ? 'Edit' : 'Comment'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock size={12} className="text-zinc-400" />
                            <span className="text-xs text-zinc-500" data-testid={`history-date-${i}`}>
                              {new Date(item._time).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} at {new Date(item._time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-zinc-600 pl-6">
                          {item._type === 'edit' 
                            ? <>Edited <span className="font-mono bg-zinc-200 px-1">{item.field || item.section}</span></>
                            : item.text
                          }
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="users" className="border-2 border-black brutal-shadow bg-white">
            <div className="p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Currently Online</h3>
              {activeUsers.length === 0 ? (
                <p className="text-center text-zinc-400 py-8">No users currently in this session</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {activeUsers.map((u, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 border border-zinc-200 bg-white">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#EAB308] to-[#E11D48] rounded-full flex items-center justify-center text-white font-bold">
                        {(u.name || u.email || "U")[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{u.name || "User"}</p>
                        <p className="text-xs text-zinc-500">{u.email}</p>
                      </div>
                      <span className="ml-auto w-2 h-2 bg-emerald-500 rounded-full" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {Object.keys(lockedSections).length > 0 && (
        <div className="border-2 border-purple-200 bg-purple-50 p-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-purple-600 mb-2">Locked Sections</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(lockedSections).map(([section, user]) => (
              <Badge key={section} className="bg-purple-100 text-purple-700 border-purple-300">
                <Lock size={12} className="mr-1" /> {section} - {user?.name || "User"}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealTimeCollabView;
